// App.js (corrigido com persistência)
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where, doc, getDoc, limit } from 'firebase/firestore';
import "@fortawesome/fontawesome-free/css/all.min.css";

// Componentes principais
import Register from './pages/Register';
import Login from './pages/Login';
import AppCreation from './pages/AppCreation';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import LandingPage from './pages/LandingPage';
import AppCreatedSuccess from './pages/AppCreatedSuccess';


// NOVAS PÁGINAS - ADICIONE ESTAS IMPORTACÕES
import TermosDeUso from './pages/termos-de-uso';
import PoliticaPrivacidade from './pages/politica-de-privacidade';
import StatusSistema from './pages/status-do-sistema';
import ContratoPage from './pages/ContratoPage';

// BLOG
import BlogList from './pages/blog/BlogList';
import BlogPost from './pages/blog/BlogPost';

// Context para gerenciar estado global
import { AppProvider } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext'; // Importação do ThemeProvider

import GlobalLoader from './components/common/GlobalLoader';
import CookieConsent from './components/common/CookieConsent';

// Estilos globais modernos
import './styles/globals.css';
import CentralAjuda from './pages/central-ajuda';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      if (user) {
        // Verificar se o usuário já tem apps criados
        try {
          const appsQuery = query(
            collection(db, 'apps'),
            where('userId', '==', user.uid)
          );
          const appsSnapshot = await getDocs(appsQuery);
          const hasApps = appsSnapshot.size > 0;

          // SALVAR NO LOCALSTORAGE para persistir entre logins
          localStorage.setItem(`user_${user.uid}_hasApps`, hasApps.toString());
        } catch (error) {
          console.error('Erro ao verificar apps do usuário:', error);
          localStorage.removeItem(`user_${user.uid}_hasApps`);
        }
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);



  // Helpers assíncronos de decisão
  // Função auxiliar de timeout
  const withTimeout = (promise, ms = 10000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
    ]);
  };

  const checkUserIsAdmin = async (uid) => {
    try {
      const userDoc = await withTimeout(getDoc(doc(db, 'users', uid)));
      return userDoc.exists() ? userDoc.data()?.isAdmin === true : false;
    } catch (error) {
      console.error("Erro ou timeout em checkUserIsAdmin:", error);
      return false;
    }
  };

  const checkUserIsBlogEditor = async (email) => {
    if (!email) return false;
    try {
      const q = query(collection(db, 'blog_editors'), where('email', '==', email), limit(1));
      const snap = await withTimeout(getDocs(q));
      return !snap.empty;
    } catch (error) {
      console.error("Erro ou timeout em checkUserIsBlogEditor:", error);
      return false;
    }
  };

  const checkUserHasAnyApp = async (uid) => {
    try {
      const appsRef = collection(db, 'apps');
      const anyQuery = query(
        appsRef,
        where('userId', '==', uid),
        limit(1)
      );
      const snap = await withTimeout(getDocs(anyQuery));
      return !snap.empty;
    } catch (error) {
      console.error("Erro ou timeout em checkUserHasAnyApp:", error);
      // Fallback conservador
      return false;
    }
  };

  // Gate que decide o destino sem piscar telas intermediárias
  const RedirectDecider = ({ user }) => {
    const [target, setTarget] = useState(null);

    useEffect(() => {
      if (!user) {
        setTarget('/login');
        return;
      }

      const decide = async () => {
        const isAdmin = await checkUserIsAdmin(user.uid);
        if (isAdmin) {
          setTarget({ to: '/admin' });
          return;
        }

        const isEditor = await checkUserIsBlogEditor(user.email);
        if (isEditor) {
          setTarget({ to: '/admin' });
          return;
        }

        const hasAnyApp = await checkUserHasAnyApp(user.uid);
        if (hasAnyApp) {
          setTarget({ to: '/dashboard' });
        } else {
          setTarget({ to: '/app-creation', state: { allowNew: true } });
        }
      };

      decide();
    }, [user]);

    if (!target) {
      return <GlobalLoader message="Carregando..." />;
    }

    return <Navigate to={target.to} state={target.state} replace />;
  };

  // Gate para bloquear acesso ao step1 quando já houver app publicado/ativo
  const AppCreationGate = ({ user, children }) => {
    const [allowed, setAllowed] = useState(null);
    const location = useLocation();

    useEffect(() => {
      if (!user) {
        setAllowed(false);
        return;
      }
      const forceNew = location?.state?.allowNew === true;
      if (forceNew) {
        setAllowed(true);
        return;
      }
      const check = async () => {
        const hasAnyApp = await checkUserHasAnyApp(user.uid);
        setAllowed(!hasAnyApp);
      };
      check();
    }, [user, location]);

    if (allowed === null) {
      return <GlobalLoader message="Verificando acesso..." />;
    }

    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  };

  // Gate para proteger /admin
  const AdminGate = ({ user, children }) => {
    const [authData, setAuthData] = useState(null);

    useEffect(() => {
      if (!user) {
        setAuthData({ isAllowed: false });
        return;
      }
      const check = async () => {
        const isAdmin = await checkUserIsAdmin(user.uid);
        if (isAdmin) {
          setAuthData({ isAllowed: true, role: 'admin' });
          return;
        }
        const isEditor = await checkUserIsBlogEditor(user.email);
        if (isEditor) {
          setAuthData({ isAllowed: true, role: 'editor' });
          return;
        }
        setAuthData({ isAllowed: false });
      };
      check();
    }, [user]);

    if (!authData) {
      return <GlobalLoader message="Verificando permissão..." />;
    }

    if (!authData.isAllowed) {
      return <Navigate to="/dashboard" replace />;
    }

    // Passar o papel do usuário para o AdminPanel
    return React.cloneElement(children, { userRole: authData.role });
  };

  if (loading) {
    return <GlobalLoader message="Carregando..." />;
  }

  return (
    <AppProvider>
      <Router future={{ v7_startTransition: true }}>
        <ThemeProvider>
          <div className="App min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <CookieConsent />
          <Routes>
            {/* PÁGINA INICIAL */}
            <Route path="/" element={<LandingPage />} />

            {/* PÁGINAS PÚBLICAS (ACESSÍVEIS SEM LOGIN) */}
            <Route path="/contrato" element={<ContratoPage />} />
            <Route path="/termos-de-uso" element={<TermosDeUso />} />
            <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/status-do-sistema" element={<StatusSistema />} />
            <Route path="/central-ajuda" element={<CentralAjuda />} />

            {/* BLOG */}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* AUTENTICAÇÃO */}
            <Route
              path="/register"
              element={!user ? <Register /> : <RedirectDecider user={user} />}
            />
            <Route
              path="/login"
              element={!user ? <Login /> : <RedirectDecider user={user} />}
            />

            {/* PÁGINAS PRIVADAS (REQUEREM LOGIN) */}
            <Route
              path="/app-creation"
              element={user ? <AppCreationGate user={user}><AppCreation /></AppCreationGate> : <Navigate to="/register" />}
            />
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/login" />}
            />

            <Route path="/admin" element={user ? <AdminGate user={user}><AdminPanel /></AdminGate> : <Navigate to="/login" />} />
            <Route
              path="/app-created-success"
              element={user ? <AppCreatedSuccess /> : <Navigate to="/login" />}
            />

            {/* Rota fallback - respeita autenticação */}
            <Route path="*" element={<Navigate to={!user ? '/login' : '/dashboard'} replace />} />
          </Routes>
        </div>
        </ThemeProvider>
      </Router>
    </AppProvider>
  );
}

export default App;